// Automatic view sanity check.
//
// Estimates whether a clip is filmed from the side (lateral) or
// toward/away (frontal) from the motion pattern, so we can warn the clinician
// if the selected view likely doesn't match the recording. Advisory only.
//
//   • Lateral: the pelvis translates a lot across the image-x axis while the
//     shoulder width stays small/stable (side-on, shoulders overlap).
//   • Frontal: little x-translation; the body scales (shoulder width changes)
//     as the patient approaches or recedes.

import { Joints, dist2D, type PoseFrame } from "../pose/landmarks";
import { percentile } from "./geometry";
import { std } from "./signal";

export type GaitView = "frontal" | "lateral";

export interface ViewCheck {
  likely: GaitView | "unknown";
  lateralTravel: number;
  shoulderWidthCv: number;
  confidence: "high" | "medium" | "low";
}

export function checkView(frames: PoseFrame[]): ViewCheck {
  if (frames.length < 6) {
    return { likely: "unknown", lateralTravel: 0, shoulderWidthCv: 0, confidence: "low" };
  }

  const pelvisX = frames.map((f) => new Joints(f).pelvisCenter().x);
  const lateralTravel = Math.max(...pelvisX) - Math.min(...pelvisX);

  const shoulderW = frames.map((f) => {
    const j = new Joints(f);
    return dist2D(j.shoulder("L"), j.shoulder("R"));
  });
  const swMed = percentile(shoulderW, 50) || 1e-4;
  const shoulderWidthCv = std(shoulderW) / swMed;

  const ratio = lateralTravel / swMed;

  let likely: GaitView | "unknown" = "unknown";
  let confidence: "high" | "medium" | "low" = "low";

  if (lateralTravel >= 0.18 && ratio >= 1.3) {
    likely = "lateral";
    confidence = lateralTravel >= 0.28 ? "high" : "medium";
  } else if (lateralTravel <= 0.1) {
    likely = "frontal";
    confidence = shoulderWidthCv >= 0.12 ? "high" : "medium";
  } else {
    likely = "unknown";
    confidence = "low";
  }

  return { likely, lateralTravel, shoulderWidthCv, confidence };
}

/** Advisory string when the detected view disagrees with the selected one. */
export function viewAdvisory(selected: GaitView, check: ViewCheck): string | null {
  if (check.likely === "unknown" || check.confidence === "low") return null;
  if (check.likely === selected) return null;
  return `This clip looks like a ${check.likely} recording, but ${selected} analysis was selected — results may be unreliable. Re-record side-on for lateral, or toward/away for frontal.`;
}
